import React, { useEffect, useRef } from 'react';
import { Badge } from '../catalyst/badge';
import type { TimelineItem } from '../types/database';
import { Wrench } from 'lucide-react';
import ImageThumbnail from './ImageThumbnail';

function formatTimestamp(iso: string): string {
	const date = new Date(iso);
	const now = new Date();
	const isToday =
		date.getFullYear() === now.getFullYear() &&
		date.getMonth() === now.getMonth() &&
		date.getDate() === now.getDate();
	if (isToday) {
		return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	}
	return (
		date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
		' ' +
		date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
	);
}

function ToolCallCard({ item }: { item: Extract<TimelineItem, { kind: 'tool_call' }> }) {
	const call = item.data;
	const result = item.result;

	return (
		<details className="mx-8 my-1 rounded-lg border border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50">
			<summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300">
				<Wrench className="h-3.5 w-3.5 text-zinc-500" />
				<span className="font-mono text-xs">{call.tool_name}</span>
				<span className="text-xs text-zinc-400">{formatTimestamp(call.created_at)}</span>
			</summary>
			<div className="border-t border-zinc-200 px-3 py-2 dark:border-zinc-700">
				<p className="mb-1 text-xs font-medium text-zinc-500">Arguments</p>
				<pre className="overflow-x-auto whitespace-pre-wrap rounded bg-zinc-100 p-2 font-mono text-xs text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
					{JSON.stringify(call.payload, null, 2)}
				</pre>
				{result && (
					<>
						<p className="mb-1 mt-2 text-xs font-medium text-zinc-500">Result</p>
						<pre className="overflow-x-auto whitespace-pre-wrap rounded bg-zinc-100 p-2 font-mono text-xs text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
							{JSON.stringify(result.payload, null, 2)}
						</pre>
					</>
				)}
			</div>
		</details>
	);
}

interface ChatTimelineProps {
	items: TimelineItem[];
}

const ChatTimeline: React.FC<ChatTimelineProps> = ({ items }) => {
	const bottomRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: 'auto' as ScrollBehavior });
	}, [items.length]);

	if (items.length === 0) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-sm text-zinc-500">No messages yet</p>
			</div>
		);
	}

	return (
		<div className="space-y-2">
			{items.map((item, i) => {
				if (item.kind === 'tool_call') {
					return <ToolCallCard key={`tool-${item.data.id}`} item={item} />;
				}

				if (item.kind === 'tool_result') {
					// Standalone results are skipped (should be paired with call)
					return null;
				}

				// Message bubble
				const msg = item.data;
				const isInbound = msg.direction === 'INBOUND';
				const isAdmin =
					msg.message_metadata &&
					(msg.message_metadata.admin_nudge_response ||
						msg.message_metadata.admin_manual ||
						msg.message_metadata.admin_nudge);

				return (
					<div
						key={msg.id}
						className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`}
					>
						<div
							className={`max-w-[75%] rounded-lg px-3 py-2 ${
								isInbound
									? 'bg-green-50 text-green-900 dark:bg-green-900/20 dark:text-green-100'
									: 'bg-blue-50 text-blue-900 dark:bg-blue-900/20 dark:text-blue-100'
							}`}
						>
							{isAdmin ? (
								<Badge color="amber" className="mb-1">
									Admin
								</Badge>
							) : null}
							{msg.content ? (
								<p className="whitespace-pre-wrap break-words text-sm">{msg.content}</p>
							) : msg.message_type === 'IMAGE' && msg.media_asset_id ? (
								<ImageThumbnail
									assetId={msg.media_asset_id}
									alt="Shared image"
									height={160}
								/>
							) : msg.message_type === 'IMAGE' ? (
								<p className="text-sm italic text-zinc-500">[Image]</p>
							) : (
								<p className="text-sm italic text-zinc-500">[{msg.message_type}]</p>
							)}
							<p
								className={`mt-1 text-right text-xs ${
									isInbound
										? 'text-green-600 dark:text-green-400'
										: 'text-blue-600 dark:text-blue-400'
								}`}
							>
								{formatTimestamp(msg.created_at)}
							</p>
						</div>
					</div>
				);
			})}
			<div ref={bottomRef} />
		</div>
	);
};

export default ChatTimeline;
